// Membership logic for putting recordings in lists: the recording page's
// "Add to list" modal and the list page's add-recording tab.
//
// The server keeps Favourites and Completed (lists with a `kind`) hike-only and
// rejects a recording there, so those lists are never offered for one.

import { sameHikeId } from './hikeListMembership';

type Id = string | number;

export interface RecordingListLike {
  id: Id;
  name?: string;
  kind?: string | null;
  itemsCount?: number;
  recordingIds?: Id[] | null;
}

// Lists belong to an account, so "Add to list" is only offered to a signed-in
// viewer — for their own recordings or someone else's public ones.
export function canSaveRecordingsToLists(user: unknown): boolean {
  return user != null && typeof user === 'object';
}

export function listsAcceptingRecordings<T extends RecordingListLike>(lists: T[]): T[] {
  return lists.filter((list) => !list.kind);
}

export function isRecordingInList(list: RecordingListLike, recordingId: Id | null | undefined): boolean {
  return list.recordingIds?.some((id) => sameHikeId(id, recordingId)) ?? false;
}

// Flips the recording's membership in one list and returns a new array, leaving
// the input untouched so the caller can roll back a failed mutation by keeping
// the old array. List order is preserved — the open modal must not shuffle.
export function toggleRecordingMembership<T extends RecordingListLike>(lists: T[], listId: Id, recordingId: Id): T[] {
  return lists.map((list) => {
    if (!sameHikeId(list.id, listId)) return list;
    const ids = list.recordingIds ?? [];
    const removing = isRecordingInList(list, recordingId);
    const count = list.itemsCount;
    return {
      ...list,
      recordingIds: removing ? ids.filter((id) => !sameHikeId(id, recordingId)) : [...ids, String(recordingId)],
      ...(count != null ? { itemsCount: Math.max(0, count + (removing ? -1 : 1)) } : {}),
    };
  });
}

// Recording search results split into those that can still be added and those
// already in the list (shown, but not clickable).
export function markRecordingsInList<T extends { id: Id }>(
  recordings: T[],
  recordingIdsInList: Id[] | null | undefined,
): Array<T & { inList: boolean }> {
  const ids = recordingIdsInList ?? [];
  return recordings.map((recording) => ({
    ...recording,
    inList: ids.some((id) => sameHikeId(id, recording.id)),
  }));
}
